import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, GraduationCap, ChevronRight, Download } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { Helmet } from "react-helmet-async";

export function Home() {
  const services = [
    {
      icon: Users,
      title: "Canais de Ética",
      description: "Sistemas seguros e anônimos para relatos e denúncias, garantindo a proteção do denunciante e a integridade da apuração."
    },
    {
      icon: ShieldCheck,
      title: "Governança",
      description: "Estruturas sólidas de políticas corporativas, gestão de riscos e conformidade legal adaptadas ao seu modelo de negócio."
    },
    {
      icon: GraduationCap,
      title: "Treinamentos",
      description: "Capacitação contínua em ética e integridade para colaboradores e lideranças, promovendo uma cultura de consciência."
    }
  ];

  const resources = [
    { type: "PDF", title: "Código de Conduta 2024: Guia de Boas Práticas", updated: "Atualizado há 2 dias" },
    { type: "Whitepaper", title: "O Impacto do ESG no Compliance Corporativo", updated: "12 páginas • PDF" },
    { type: "Checklist", title: "Auditoria Interna: Passo a Passo para o Sucesso", updated: "Essential Toolkit" },
    { type: "Infográfico", title: "LGPD: Entendendo os pilares da privacidade", updated: "Visual Guide" }
  ];

  return (
    <>
      <Helmet>
        <title>Portal Ethics - Liderando com Integridade</title>
        <meta name="description" content="Soluções completas em ética, compliance e governança corporativa para transformar sua cultura organizacional com transparência e segurança." />
      </Helmet>
      
      <div className="min-h-screen bg-background-light">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative h-[640px] flex items-center overflow-hidden">
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
                alt="Corporate building"
                className="object-cover w-full h-full"
              />
            </div>
            
            <div className="relative z-20 max-w-7xl mx-auto px-6 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/20 text-white rounded-full mb-6 border border-white/10 backdrop-blur-sm">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-wider uppercase">Compliance 360°</span>
                </div>
                
                <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                  Liderando com <br />
                  <span className="italic text-blue-200">Integridade</span>
                </h1>
                
                <p className="text-lg text-white/80 mb-10 leading-relaxed font-light">
                  Soluções completas em ética, compliance e governança corporativa para transformar sua cultura organizacional com transparência e segurança.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="group">
                    Falar com Especialista
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Link to="/planos">
                    <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/20 bg-white/10 backdrop-blur-md">
                      Ver Planos
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
                <div className="max-w-xl">
                  <h2 className="text-primary-blue font-bold tracking-widest text-sm uppercase mb-3 flex items-center gap-2">
                    <span className="w-8 h-px bg-primary-blue"></span> Nossos Serviços
                  </h2>
                  <h3 className="text-4xl font-extrabold text-neutral-900 leading-tight">
                    Soluções integradas para excelência em conformidade.
                  </h3>
                </div>
                <p className="text-neutral-500 max-w-xs text-sm leading-relaxed mb-1">
                  Auxiliamos empresas a construir ambientes de trabalho éticos e resilientes através de tecnologia e consultoria especializada.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <div key={index} className="group p-8 rounded-2xl bg-background-light border border-transparent hover:border-primary-blue/20 hover:bg-white hover:shadow-xl transition-all">
                      <div className="w-14 h-14 bg-primary-blue/10 text-primary-blue rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-blue group-hover:text-white transition-colors">
                        <Icon className="w-7 h-7" />
                      </div>
                      <h4 className="text-xl font-bold mb-4">{service.title}</h4>
                      <p className="text-neutral-600 leading-relaxed mb-6">
                        {service.description}
                      </p>
                      <a href="#" className="inline-flex items-center text-primary-blue font-bold text-sm gap-2 hover:gap-3 transition-all">
                        Saiba mais <ChevronRight className="w-5 h-5" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Educational Resources */}
          <section className="py-24 bg-background-light">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex items-center justify-between mb-12">
                <div>
                  <h2 className="text-primary-blue font-bold tracking-widest text-sm uppercase mb-3">Biblioteca Digital</h2>
                  <h3 className="text-3xl font-bold">Recursos Educativos</h3>
                </div>
                <button className="text-primary-blue font-bold flex items-center gap-2 hover:underline">
                  Ver todos recursos <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {resources.map((resource, index) => (
                  <div key={index} className="bg-white rounded-xl overflow-hidden border border-neutral-100 hover:shadow-md transition-all group">
                    <div className="aspect-[3/4] relative bg-gradient-to-br from-primary-blue/10 to-primary-blue/5 flex items-center justify-center">
                      <div className="text-center p-6">
                        <span className="text-6xl font-black text-primary-blue/20">{resource.type[0]}</span>
                      </div>
                      <div className="absolute inset-0 bg-primary-blue/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <button className="bg-white text-primary-blue p-3 rounded-full shadow-lg hover:scale-110 transition-transform">
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="p-5">
                      <span className="text-[10px] font-bold text-primary-blue uppercase bg-primary-blue/5 px-2 py-0.5 rounded">{resource.type}</span>
                      <h5 className="mt-2 font-bold text-sm line-clamp-2">{resource.title}</h5>
                      <p className="text-xs text-neutral-500 mt-2">{resource.updated}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-primary-blue relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
            <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                Pronto para elevar o nível de conformidade da sua empresa?
              </h2>
              <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
                Junte-se a centenas de organizações que confiam no Portal Ethics para gerenciar seus canais de denúncia e políticas de governança.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-primary-blue hover:bg-neutral-100">
                  Solicitar Demonstração
                </Button>
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 bg-transparent">
                  Baixar Mídia Kit
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
